import React, { useState } from "react";
import { Form, Formik } from "formik";
import { useParams } from "react-router-dom";
import {
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  Stack,
  Text,
} from "@fluentui/react";
import CobrandTable from "./OptionTables/CobrandTable";
import CobrandTagsTable from "./OptionTables/CobrandTagsTable";
import ClientsTable from "./OptionTables/ClientsTable";
import { Card } from "@uifabric/react-cards";
import OnOffSettingsFields from "./OnOffSettingsFields";
import ValidationErrorsModal from "../ValidationErrorsModal";
import { useOnOffSettingsSubmit } from "./useOnOffSettingsSubmit";
import FeatureSummary from "../../../../Util/FeatureSummary";

const OnOffSettingsPage = () => {
  const { checklistId } = useParams();
  const [optionTable, setOptionTable] = useState();

  const {
    onOffSetting,
    loadingOnOffSetting,
    loading,
    errorMessage,
    setErrorMessage,
    successMessage,
    setSuccessMessage,
    validationErrors,
    showValidationModal,
    setShowValidationModal,
    cobrandOptions,
    handleSubmit,
    handleExcludeCobrands,
  } = useOnOffSettingsSubmit(checklistId);

  if (loadingOnOffSetting) {
    return (
      <Spinner className="absolute-center" label="Retrieving Settings..." />
    );
  }

  return (
    <Formik
      initialValues={onOffSetting}
      onSubmit={async (values) => {
        await handleSubmit(values);
      }}
    >
      {({ handleSubmit: formikSubmit, isSubmitting, values, setFieldValue }) => (
        <Form>
          <Stack horizontal tokens={{ childrenGap: 30 }}>
            <Stack
              tokens={{ childrenGap: 20 }}
              styles={{
                root: {
                  width: 300,
                  paddingTop: 20,
                },
              }}
            >
              {successMessage && (
                <MessageBar
                  messageBarType={MessageBarType.success}
                  onDismiss={() => setSuccessMessage(null)}
                >
                  {successMessage}
                </MessageBar>
              )}
              {errorMessage && (
                <MessageBar
                  messageBarType={MessageBarType.error}
                  onDismiss={() => setErrorMessage(null)}
                >
                  {errorMessage}
                </MessageBar>
              )}
              <OnOffSettingsFields setOptionTable={setOptionTable} />
              <PrimaryButton
                type="submit"
                onClick={formikSubmit}
                disabled={isSubmitting || loading}
              >
                <Stack
                  tokens={{ childrenGap: 10 }}
                  verticalAlign="center"
                  horizontal
                >
                  {isSubmitting || loading ? (
                    <Spinner size={SpinnerSize.small} />
                  ) : (
                    <Text>Save</Text>
                  )}
                </Stack>
              </PrimaryButton>
            </Stack>
            {optionTable && (
              <Card
                tokens={{
                  padding: 25,
                  maxWidth: "100%",
                }}
                styles={{
                  root: {
                    alignContent: "center",
                  },
                }}
              >
                <OptionsTable
                  optionTable={optionTable}
                  setOptionTable={setOptionTable}
                />
              </Card>
            )}
          </Stack>
          <FeatureSummary
              label="About Checklist On/Off Settings"
              summary="Control checklist visibility across your organization. Set the global default (on or off), then add exceptions for specific clients, cobrands, or cobrand tags. Checklists will only appear for users whose cobrand matches the on/off configuration."
          />
          <ValidationErrorsModal
            isOpen={showValidationModal}
            onDismiss={() => setShowValidationModal(false)}
            validationErrors={validationErrors}
            isGlobalOn={values.globalOn}
            cobrandOptions={cobrandOptions}
            onExcludeCobrands={(cobrandIds) =>
              handleExcludeCobrands(cobrandIds, values, setFieldValue)
            }
          />
        </Form>
      )}
    </Formik>
  );
};

const OptionsTable = ({ optionTable, setOptionTable }) => {
  switch (optionTable) {
    case "cobrandTag":
      return <CobrandTagsTable setOptionTable={setOptionTable} />;
    case "cobrand":
      return <CobrandTable setOptionTable={setOptionTable} />;
    case "client":
      return <ClientsTable setOptionTable={setOptionTable} />;
    default:
      return null;
  }
};

export default OnOffSettingsPage;
